var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect, useMemo, useRef, useState } from "react";
import { debugLog } from "@lululemon/auth-common";
import { useUserActivity } from "./use-user-activity";
import { checkAndRefreshSession } from "../utils/session-utils";
export function useSessionRefresh(_a) {
    var _this = this;
    var authenticationStatus = _a.authenticationStatus, authSdk = _a.authSdk, oktaConfig = _a.oktaConfig, shapeConfig = _a.shapeConfig, skipSessionRefresh = _a.skipSessionRefresh, updateAuth = _a.updateAuth;
    var sessionRefreshPromise = useRef(null);
    var _b = __read(useState(skipSessionRefresh ? "skipped" : "pending"), 2), sessionRefreshStatus = _b[0], setSessionRefreshStatus = _b[1];
    var isUserActive = useUserActivity({
        isEnabled: !skipSessionRefresh,
        onUserActive: function () {
            setSessionRefreshStatus("pending");
            updateAuth();
        }
    }).isUserActive;
    // This useEffect checks the user's current state and refreshes the session if needed
    useEffect(function () {
        // If LaunchDarkly flag is false, do nothing
        if (skipSessionRefresh || sessionRefreshPromise.current || !isUserActive) {
            return;
        }
        // If user is authenticated, we're done!
        if (authenticationStatus === "authenticated") {
            debugLog({ message: "User is authenticated." });
            setSessionRefreshStatus("complete");
            return;
        }
        var tryRefreshSession = function () { return __awaiter(_this, void 0, void 0, function () {
            var e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        setSessionRefreshStatus("pending");
                        return [4 /*yield*/, checkAndRefreshSession({ oktaConfig: oktaConfig, shapeConfig: shapeConfig })];
                    case 1:
                        _a.sent();
                        setSessionRefreshStatus("complete");
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        // NOTE: Session refresh errors are logged in refreshSession()
                        setSessionRefreshStatus("failed");
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        // If we have HttpOnly cookies, Shape has loaded, and the user is unauthenticated - refresh!
        if (authenticationStatus === "unauthenticated") {
            sessionRefreshPromise.current = tryRefreshSession().finally(function () {
                sessionRefreshPromise.current = null;
            });
        }
    }, [authenticationStatus, isUserActive, skipSessionRefresh]);
    // Once the session refresh process has finished, fire window event for analytics and other systems
    useEffect(function () {
        var onSessionRefreshComplete = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        debugLog({
                            message: "Session refresh ".concat(sessionRefreshStatus, ", dispatching window event.")
                        });
                        return [4 /*yield*/, updateAuth()];
                    case 1:
                        _a.sent();
                        // Fire window event to notify other systems (analytics, bag count, etc.) that session refresh is complete
                        window.oktaSessionRefreshComplete = true;
                        window.dispatchEvent(new CustomEvent("okta-session-refresh-complete"));
                        authSdk.refreshStates();
                        return [2 /*return*/];
                }
            });
        }); };
        if (sessionRefreshStatus === "complete" ||
            sessionRefreshStatus === "failed") {
            onSessionRefreshComplete();
        }
    }, [sessionRefreshStatus]);
    return {
        sessionRefreshStatus: sessionRefreshStatus,
        isSessionProcessing: useMemo(function () {
            return !(sessionRefreshStatus === "complete" ||
                (sessionRefreshStatus === "failed" &&
                    authenticationStatus === "unauthenticated"));
        }, [authenticationStatus, sessionRefreshStatus])
    };
}
